import React, {
    useState,
    useRef,
    useId,
    useCallback,
    useEffect,
    useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { Text } from '@fragments';
import './styles.scss';

/**
 * Tooltip — a lightweight, dependency-free hover/focus tooltip.
 *
 * Wraps a single child and shows a small floating chip on mouseenter/focus
 * (after `delay` ms), hiding on mouseleave/blur. The bubble is rendered in a
 * portal to <body> so it escapes any ancestor `overflow`/`transform`/`clip-path`.
 * Positioning mirrors the Popper fragment: getBoundingClientRect on the trigger,
 * an 8px gap for the chosen placement, plus viewport clamping.
 *
 * @param {React.ReactNode} content     - chip content (string or node). Empty/null → no tooltip.
 * @param {'top'|'bottom'|'left'|'right'} placement - preferred side (default 'top').
 * @param {number} delay                - ms to wait before showing (default 250).
 * @param {boolean} arrow               - render the small pointer arrow (default true).
 * @param {boolean} disabled            - when true, render the child with no tooltip.
 * @param {string} className            - extra classes for the chip.
 * @param {React.ReactNode} children    - the single trigger element.
 */
export default function Tooltip({
    content,
    placement = 'top',
    delay = 250,
    arrow = true,
    disabled = false,
    className = '',
    children,
    ...rest
}) {
    const reactId = useId();
    const describedById = `tooltip-${reactId.replace(/[:]/g, '')}`;

    const triggerRef = useRef(null);
    const [bubbleEl, setBubbleEl] = useState(null);
    const showTimerRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, side: placement, ready: false });

    const isEmpty = content === null || content === undefined || content === '';
    const enabled = !disabled && !isEmpty;

    // ── Portal target: a <body>-level node so the chip escapes ancestor clipping.
    const portalNodeRef = useRef(null);
    if (typeof document !== 'undefined' && portalNodeRef.current === null) {
        portalNodeRef.current = document.createElement('div');
        portalNodeRef.current.className = 'tooltip-portal';
    }
    useEffect(() => {
        const node = portalNodeRef.current;
        if (!node || typeof document === 'undefined') return undefined;
        document.body.appendChild(node);
        return () => {
            if (node.parentNode) node.parentNode.removeChild(node);
        };
    }, []);

    const clearShowTimer = useCallback(() => {
        if (showTimerRef.current) {
            clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }
    }, []);

    const scheduleShow = useCallback(() => {
        if (!enabled) return;
        clearShowTimer();
        showTimerRef.current = setTimeout(() => {
            showTimerRef.current = null;
            setOpen(true);
        }, Math.max(0, delay));
    }, [enabled, delay, clearShowTimer]);

    const hide = useCallback(() => {
        clearShowTimer();
        setOpen(false);
    }, [clearShowTimer]);

    // Clean up any pending timer on unmount.
    useEffect(() => clearShowTimer, [clearShowTimer]);

    // If the tooltip becomes disabled / empty while open, close it.
    useEffect(() => {
        if (!enabled && open) hide();
    }, [enabled, open, hide]);

    // ── Position the chip relative to the trigger. Recomputes on open, on the
    // bubble mounting (to measure it), and on scroll/resize while open. The
    // arrow stays pointed at the trigger even after viewport clamping nudges
    // the chip, via the resolved `side`.
    useLayoutEffect(() => {
        const anchorEl = triggerRef.current;
        if (!open || !anchorEl || !bubbleEl) {
            setPosition((prev) => ({ ...prev, ready: false }));
            return undefined;
        }

        function compute() {
            const anchorRect = anchorEl.getBoundingClientRect();
            const bubbleRect = bubbleEl.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const gap = 8;
            const margin = 8;

            let side = placement;

            // Flip to the opposite side if there isn't room on the preferred one.
            if (side === 'top' && anchorRect.top - bubbleRect.height - gap < margin) {
                side = 'bottom';
            } else if (side === 'bottom' && anchorRect.bottom + bubbleRect.height + gap > vh - margin) {
                side = 'top';
            } else if (side === 'left' && anchorRect.left - bubbleRect.width - gap < margin) {
                side = 'right';
            } else if (side === 'right' && anchorRect.right + bubbleRect.width + gap > vw - margin) {
                side = 'left';
            }

            let top;
            let left;

            if (side === 'top') {
                top = anchorRect.top - bubbleRect.height - gap;
                left = anchorRect.left + (anchorRect.width - bubbleRect.width) / 2;
            } else if (side === 'bottom') {
                top = anchorRect.bottom + gap;
                left = anchorRect.left + (anchorRect.width - bubbleRect.width) / 2;
            } else if (side === 'left') {
                top = anchorRect.top + (anchorRect.height - bubbleRect.height) / 2;
                left = anchorRect.left - bubbleRect.width - gap;
            } else { // right
                top = anchorRect.top + (anchorRect.height - bubbleRect.height) / 2;
                left = anchorRect.right + gap;
            }

            // Clamp into the viewport (arrow keeps pointing at the trigger center).
            left = Math.max(margin, Math.min(left, vw - bubbleRect.width - margin));
            top = Math.max(margin, Math.min(top, vh - bubbleRect.height - margin));

            setPosition({ top, left, side, ready: true });
        }

        compute();
        window.addEventListener('scroll', compute, true);
        window.addEventListener('resize', compute);
        return () => {
            window.removeEventListener('scroll', compute, true);
            window.removeEventListener('resize', compute);
        };
    }, [open, bubbleEl, placement]);

    if (!React.isValidElement(children)) {
        // Tooltip needs a single element to anchor to / attach handlers on.
        return children ?? null;
    }

    // Compose our trigger handlers with any the child already declares, and
    // attach the anchor ref (merging a possible existing ref).
    const childRef = children.ref;
    const setTriggerRef = (node) => {
        triggerRef.current = node;
        if (typeof childRef === 'function') childRef(node);
        else if (childRef && typeof childRef === 'object') childRef.current = node;
    };

    const compose = (ours, theirs) => (event) => {
        ours(event);
        if (typeof theirs === 'function') theirs(event);
    };

    const triggerProps = enabled
        ? {
            ref: setTriggerRef,
            'aria-describedby': open ? describedById : undefined,
            onMouseEnter: compose(scheduleShow, children.props.onMouseEnter),
            onMouseLeave: compose(hide, children.props.onMouseLeave),
            onFocus: compose(scheduleShow, children.props.onFocus),
            onBlur: compose(hide, children.props.onBlur),
        }
        : {};

    const trigger = React.cloneElement(children, triggerProps);

    const bubble = (
        <div
            ref={setBubbleEl}
            id={describedById}
            role="tooltip"
            className={`tooltip ${arrow ? 'tooltip--arrow' : ''} tooltip--${position.side} ${className}`.replace(/\s+/g, ' ').trim()}
            style={{
                top: position.top,
                left: position.left,
                visibility: position.ready ? 'visible' : 'hidden',
            }}
            {...rest}
        >
            <Text.caption contrast={10} className="tooltip__label">
                {content}
            </Text.caption>
            {arrow && <span className="tooltip__arrow" aria-hidden="true" />}
        </div>
    );

    return (
        <>
            {trigger}
            {enabled && open && portalNodeRef.current
                ? createPortal(bubble, portalNodeRef.current)
                : null}
        </>
    );
}
